import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { InfoTooltip } from '@components/common/InfoTooltip';
import type { LineItemReview, ExpenseAccount } from '@schemas/api';

interface ItemSelectionModalProps {
  lineItem: LineItemReview;
  expenseAccounts: ExpenseAccount[];
  onSelectItem: (itemId: string) => void;
  onCreateItem: (itemData: {
    name: string;
    account_ref: string;
    sku?: string;
    description?: string;
  }) => void;
  onCancel: () => void;
}

export const ItemSelectionModal: React.FC<ItemSelectionModalProps> = ({
  lineItem,
  expenseAccounts,
  onCreateItem,
  onCancel,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: lineItem.description,
    account_ref: expenseAccounts[0]?.id || '',
    sku: '',
    description: lineItem.description,
  });

  const handleCreateNew = () => {
    if (!formData.name || !formData.account_ref) {
      alert('Please fill in required fields (Name and Account)');
      return;
    }

    onCreateItem({
      name: formData.name,
      account_ref: formData.account_ref,
      sku: formData.sku || undefined,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isCreating ? 'Create New Item' : 'Item Options'}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {!isCreating ? (
            /* Tab Selection */
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-4">
                How would you like to proceed?
              </p>

              <button
                onClick={() => setIsCreating(true)}
                className="w-full px-4 py-3 border border-blue-300 rounded-lg hover:bg-blue-50 transition text-left"
              >
                <div className="font-medium text-blue-900 mb-1">
                  Create New Item
                </div>
                <div className="text-sm text-blue-700">
                  Create a new item in QB for this line
                </div>
              </button>

              <div className="my-4 text-center text-sm text-gray-500">or</div>

              <p className="text-sm text-gray-600">
                Select one of the LLM suggestions to use instead
              </p>
            </div>
          ) : (
            /* Create Form */
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateNew();
              }}
              className="space-y-4"
            >
              {/* Item Name */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Service Labor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  SKU (Optional)
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({ ...formData, sku: e.target.value })
                  }
                  placeholder="e.g., SRV-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Expense Account */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Expense Account *
                </label>
                <SearchableSelect
                  value={formData.account_ref}
                  onChange={(value) =>
                    setFormData({ ...formData, account_ref: value })
                  }
                  options={expenseAccounts.map((account) => ({
                    id: account.id,
                    label: `${account.name} (${account.account_number})`,
                  }))}
                  placeholder="Expense account"
                  clearable={false}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Description of the item"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Info Box */}
              <div className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                <span>QuickBooks item type</span>
                <InfoTooltip>AP items are created as Service. SKU is optional and can be added later.</InfoTooltip>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => {
              if (isCreating) {
                setIsCreating(false);
              } else {
                onCancel();
              }
            }}
            className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-medium hover:bg-gray-50 transition"
          >
            {isCreating ? 'Back' : 'Cancel'}
          </button>
          {isCreating && (
            <button
              onClick={handleCreateNew}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
            >
              Create Item
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemSelectionModal;
